import React, { useState } from "react";
import {
    Plus,
    Edit,
    Trash2,
    Eye,
    X,
    GripVertical,
    CheckCircle2,
    PlusCircle,
} from "lucide-react";

export default function SurveyBuilder() {
    const QUESTION_TEMPLATES = {
        short: { type: "short", question: "Short Answer Question", required: false },
        long: { type: "long", question: "Long Answer Question", required: false },
        mcq: { type: "mcq", question: "Multiple Choice Question", required: false, options: ["Option 1"] },
        checkbox: { type: "checkbox", question: "Checkbox Question", required: false, options: ["Option 1"] },
        rating: { type: "rating", question: "Rating Question", required: false, scale: 5 },
    };

    const [survey, setSurvey] = useState({
        title: "",
        description: "",
        category: "",
        active: true,
    });

    const [questions, setQuestions] = useState([]);

    const [previewMode, setPreviewMode] = useState(false);

    const [editModal, setEditModal] = useState({
        open: false,
        index: null,
        data: null,
    });

    const addQuestion = (type) => {
        setQuestions([...questions, { ...QUESTION_TEMPLATES[type], id: Date.now() }]);
    };

    const deleteQuestion = (id) => {
        if (!window.confirm("Delete this question?")) return;
        setQuestions(questions.filter((q) => q.id !== id));
    };

    const updateQuestion = () => {
        const updatedList = [...questions];
        updatedList[editModal.index] = editModal.data;
        setQuestions(updatedList);
        closeModal();
    };

    const openModal = (index) => {
        setEditModal({
            open: true,
            index,
            data: { ...questions[index] },
        });
    };

    const closeModal = () => {
        setEditModal({ open: false, index: null, data: null });
    };

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen font-['Poppins','Roboto',sans-serif]">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800">Survey Builder</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Create surveys, add questions, and customize responses.
                    </p>
                </div>

                <button
                    onClick={() => setPreviewMode(!previewMode)}
                    className="px-4 py-2 flex items-center bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700"
                >
                    <Eye size={18} className="mr-2" /> {previewMode ? "Close Preview" : "Preview Survey"}
                </button>
            </div>

            {/* Meta Form */}
            {!previewMode && (
                <div className="bg-white p-6 rounded-xl shadow mb-8 border">
                    <h2 className="text-lg font-semibold mb-4">Survey Info</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm text-gray-600">Survey Title</label>
                            <input
                                className="w-full p-2 border rounded mt-1"
                                value={survey.title}
                                onChange={(e) => setSurvey({ ...survey, title: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="text-sm text-gray-600">Category</label>
                            <input
                                className="w-full p-2 border rounded mt-1"
                                value={survey.category}
                                onChange={(e) => setSurvey({ ...survey, category: e.target.value })}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="text-sm text-gray-600">Description</label>
                            <textarea
                                rows="3"
                                className="w-full p-2 border rounded mt-1"
                                value={survey.description}
                                onChange={(e) => setSurvey({ ...survey, description: e.target.value })}
                            ></textarea>
                        </div>
                    </div>
                </div>
            )}

            {/* Question Builder */}
            {!previewMode && (
                <>
                    {/* Add Question Buttons */}
                    <div className="bg-white p-6 rounded-xl shadow border mb-6">
                        <h2 className="text-lg font-semibold mb-4">Add Question</h2>

                        <div className="flex flex-wrap gap-3">
                            {Object.keys(QUESTION_TEMPLATES).map((type) => (
                                <button
                                    key={type}
                                    onClick={() => addQuestion(type)}
                                    className="flex items-center px-3 py-2 bg-teal-600 text-white rounded shadow hover:bg-teal-700 text-sm"
                                >
                                    <PlusCircle size={16} className="mr-2" />
                                    {QUESTION_TEMPLATES[type].question}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Questions List */}
                    <div className="bg-white p-6 rounded-xl shadow border">
                        <h2 className="text-lg font-semibold mb-4">Questions</h2>

                        {questions.length === 0 ? (
                            <p className="text-gray-400 italic">No questions added yet.</p>
                        ) : (
                            <div className="space-y-4">
                                {questions.map((q, index) => (
                                    <div
                                        key={q.id}
                                        className="p-4 bg-gray-50 border rounded-xl shadow-sm flex justify-between items-center"
                                    >
                                        <div className="flex items-center gap-3">
                                            <GripVertical size={20} className="text-gray-400" />
                                            <div>
                                                <p className="font-medium text-gray-800">{q.question}</p>
                                                <p className="text-xs text-gray-500">{q.type.toUpperCase()}</p>
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openModal(index)}
                                                className="text-indigo-600 hover:text-indigo-800"
                                            >
                                                <Edit size={18} />
                                            </button>

                                            <button
                                                onClick={() => deleteQuestion(q.id)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Preview Mode */}
            {previewMode && (
                <div className="bg-white p-6 rounded-xl shadow border">
                    <h2 className="text-xl font-bold">{survey.title || "Untitled Survey"}</h2>
                    <p className="text-gray-500 mb-4">{survey.description}</p>

                    <div className="space-y-6">
                        {questions.map((q, i) => (
                            <div key={q.id}>
                                <label className="font-medium text-gray-700">{i + 1}. {q.question}</label>

                                {q.type === "short" && (
                                    <input className="w-full mt-2 p-2 border rounded" placeholder="Short answer" />
                                )}

                                {q.type === "long" && (
                                    <textarea className="w-full mt-2 p-2 border rounded" rows={3}></textarea>
                                )}

                                {q.type === "mcq" && (
                                    <div className="mt-2 space-y-2">
                                        {q.options.map((op, idx) => (
                                            <div key={idx} className="flex items-center gap-2">
                                                <input type="radio" />
                                                <span>{op}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {q.type === "checkbox" && (
                                    <div className="mt-2 space-y-2">
                                        {q.options.map((op, idx) => (
                                            <div key={idx} className="flex items-center gap-2">
                                                <input type="checkbox" />
                                                <span>{op}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {q.type === "rating" && (
                                    <div className="flex gap-2 mt-2">
                                        {[...Array(q.scale)].map((_, n) => (
                                            <CheckCircle2 key={n} size={24} className="text-yellow-500" />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Edit Question Modal */}
            {editModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
                    <div className="bg-white w-full max-w-2xl p-6 rounded-xl shadow-xl relative">
                        <button
                            className="absolute right-4 top-4 text-gray-600"
                            onClick={closeModal}
                        >
                            <X size={22} />
                        </button>

                        <h2 className="text-xl font-semibold mb-4">Edit Question</h2>

                        <label className="text-sm font-medium">Question Text</label>
                        <input
                            className="w-full p-2 border rounded mt-1"
                            value={editModal.data.question}
                            onChange={(e) =>
                                setEditModal({
                                    ...editModal,
                                    data: { ...editModal.data, question: e.target.value },
                                })
                            }
                        />

                        {/* MCQ / Checkbox Options */}
                        {(editModal.data.type === "mcq" || editModal.data.type === "checkbox") && (
                            <div className="mt-4">
                                <label className="text-sm font-medium">Options</label>

                                {editModal.data.options.map((op, idx) => (
                                    <div key={idx} className="flex gap-2 mt-2">
                                        <input
                                            className="flex-1 p-2 border rounded"
                                            value={op}
                                            onChange={(e) => {
                                                const o = [...editModal.data.options];
                                                o[idx] = e.target.value;
                                                setEditModal({
                                                    ...editModal,
                                                    data: { ...editModal.data, options: o },
                                                });
                                            }}
                                        />
                                        <button
                                            onClick={() => {
                                                let o = [...editModal.data.options];
                                                o.splice(idx, 1);
                                                setEditModal({
                                                    ...editModal,
                                                    data: { ...editModal.data, options: o },
                                                });
                                            }}
                                            className="text-red-600"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    onClick={() =>
                                        setEditModal({
                                            ...editModal,
                                            data: {
                                                ...editModal.data,
                                                options: [...editModal.data.options, "New Option"],
                                            },
                                        })
                                    }
                                    className="mt-3 px-3 py-1 bg-teal-600 text-white rounded"
                                >
                                    Add Option
                                </button>
                            </div>
                        )}

                        {/* Rating Scale */}
                        {editModal.data.type === "rating" && (
                            <div className="mt-4">
                                <label className="text-sm font-medium">Rating Scale</label>
                                <input
                                    type="number"
                                    min="3"
                                    max="10"
                                    className="p-2 border rounded mt-1"
                                    value={editModal.data.scale}
                                    onChange={(e) =>
                                        setEditModal({
                                            ...editModal,
                                            data: { ...editModal.data, scale: Number(e.target.value) },
                                        })
                                    }
                                />
                            </div>
                        )}

                        {/* Required Toggle */}
                        <div className="flex items-center gap-2 mt-4">
                            <input
                                type="checkbox"
                                checked={editModal.data.required}
                                onChange={(e) =>
                                    setEditModal({
                                        ...editModal,
                                        data: { ...editModal.data, required: e.target.checked },
                                    })
                                }
                            />
                            <span>Required Question</span>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                className="px-4 py-2 bg-gray-300 rounded"
                                onClick={closeModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-teal-600 text-white rounded"
                                onClick={updateQuestion}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
